// ============================================================
// utils/calculations.js
// Lógica de negocio: cálculo de costos de impresión 3D
// Todas las funciones son puras (sin efectos secundarios)
// ============================================================

/**
 * Convierte Watts a kiloWatts.
 * @param {number} watts - Potencia en Watts
 * @returns {number} Potencia en kW
 */
export function wattsToKw(watts) {
  return watts / 1000;
}

/**
 * Calcula el costo de electricidad para una impresión.
 *
 * Fórmula: Costo_Luz = Potencia_kW × Tiempo_horas × Precio_kWh
 *
 * @param {number} powerKw     - Potencia de la impresora en kW
 * @param {number} hours       - Duración de la impresión en horas
 * @param {number} kwPrice     - Precio por kWh en la moneda local
 * @returns {number} Costo total de electricidad
 */
export function calculateElectricityCost(powerKw, hours, kwPrice) {
  if (!powerKw || !hours || !kwPrice) return 0;
  return powerKw * hours * kwPrice;
}

/**
 * Calcula el costo del material (filamento) utilizado.
 *
 * Fórmula: Costo_Material = (Precio_kg / 1000) × Peso_gramos
 *
 * @param {number} pricePerKg  - Precio del filamento por kg
 * @param {number} usedGrams   - Gramos de filamento consumidos
 * @returns {number} Costo del material usado
 */
export function calculateMaterialCost(pricePerKg, usedGrams) {
  if (!pricePerKg || !usedGrams) return 0;
  const pricePerGram = pricePerKg / 1000;
  return pricePerGram * usedGrams;
}

/**
 * Calcula los costos adicionales totales por hora de operación.
 * Incluye: mantenimiento + depreciación + mano de obra
 *
 * Fórmula: Costo_Adicional = (Mantenimiento + Depreciacion) × Horas + Mano_Obra
 *
 * @param {number} maintenancePerHour  - Costo de mantenimiento por hora
 * @param {number} depreciationPerHour - Costo de depreciación por hora
 * @param {number} laborCost           - Costo fijo de mano de obra por trabajo
 * @param {number} hours               - Duración de la impresión en horas
 * @returns {{ maintenance: number, depreciation: number, labor: number, total: number }}
 */
export function calculateAdditionalCosts(
  maintenancePerHour,
  depreciationPerHour,
  laborCost,
  hours
) {
  const maintenance  = (maintenancePerHour  || 0) * hours;
  const depreciation = (depreciationPerHour || 0) * hours;
  const labor        = laborCost || 0;

  return {
    maintenance,
    depreciation,
    labor,
    total: maintenance + depreciation + labor,
  };
}

/**
 * Aplica un margen de ganancia sobre el costo base.
 *
 * Fórmula: Precio_Final = Costo_Base × (1 + Margen% / 100)
 *
 * @param {number} baseCost       - Costo total sin margen
 * @param {number} marginPercent  - Porcentaje de ganancia (ej: 30 para 30%)
 * @returns {{ finalPrice: number, profitAmount: number }}
 */
export function applyProfitMargin(baseCost, marginPercent) {
  if (!baseCost) return { finalPrice: 0, profitAmount: 0 };
  const margin      = marginPercent || 0;
  const profitAmount = baseCost * (margin / 100);
  const finalPrice   = baseCost + profitAmount;
  return { finalPrice, profitAmount };
}

/**
 * Función principal que orquesta todos los cálculos.
 * Devuelve un objeto completo con desglose detallado y resumen.
 *
 * @param {Object} params - Parámetros de la impresión
 * @param {number} params.powerWatts         - Potencia en Watts (se convierte internamente)
 * @param {number} params.hours              - Horas de impresión
 * @param {number} params.kwPrice            - Precio por kWh
 * @param {number} params.filamentPricePerKg - Precio del filamento por kg
 * @param {number} params.usedGrams          - Gramos usados
 * @param {number} params.maintenancePerHour - Mantenimiento por hora
 * @param {number} params.depreciationPerHour- Depreciación por hora
 * @param {number} params.laborCost          - Mano de obra fija
 * @param {number} params.marginPercent      - Margen de ganancia %
 * @returns {Object} Resultado completo con cada componente de costo
 */
export function calculateTotalCost(params) {
  const {
    powerWatts,
    hours,
    kwPrice,
    filamentPricePerKg,
    usedGrams,
    maintenancePerHour,
    depreciationPerHour,
    laborCost,
    marginPercent,
  } = params;

  // 1. Conversión de unidades
  const powerKw = wattsToKw(powerWatts || 0);

  // 2. Costo de electricidad
  const electricityCost = calculateElectricityCost(powerKw, hours, kwPrice);

  // 3. Costo de material
  const materialCost = calculateMaterialCost(filamentPricePerKg, usedGrams);

  // 4. Costos adicionales desglosados
  const additional = calculateAdditionalCosts(
    maintenancePerHour,
    depreciationPerHour,
    laborCost,
    hours
  );

  // 5. Subtotal sin margen
  const subtotal = electricityCost + materialCost + additional.total;

  // 6. Precio final con margen de ganancia
  const { finalPrice, profitAmount } = applyProfitMargin(subtotal, marginPercent);

  // 7. Cálculo del costo por gramo (útil como referencia)
  const costPerGram = usedGrams > 0 ? finalPrice / usedGrams : 0;

  return {
    // Datos de entrada procesados
    powerKw,
    energyConsumedKwh: powerKw * (hours || 0),   // kWh totales consumidos

    // Costos individuales
    electricityCost,
    materialCost,
    maintenanceCost:   additional.maintenance,
    depreciationCost:  additional.depreciation,
    laborCost:         additional.labor,
    additionalTotal:   additional.total,

    // Totales
    subtotal,
    profitAmount,
    finalPrice,

    // Métricas adicionales
    costPerGram,
    marginPercent: marginPercent || 0,
  };
}

/**
 * Formatea un número como moneda según el código ISO y decimales.
 *
 * @param {number} value    - Valor numérico a formatear
 * @param {string} currency - Código ISO de la moneda (ej: 'COP', 'USD')
 * @param {string} symbol   - Símbolo de la moneda
 * @param {number} decimals - Número de decimales
 * @returns {string} Valor formateado como texto
 */
export function formatCurrency(value, symbol, decimals) {
  if (isNaN(value) || value === null) return `${symbol}0`;

  const formatted = new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

  return `${symbol}${formatted}`;
}

/**
 * Construye el mensaje de WhatsApp sustituyendo los placeholders.
 *
 * @param {string} template   - Plantilla con {{PLACEHOLDER}} markers
 * @param {Object} data       - Objeto con los valores a sustituir
 * @returns {string} Mensaje listo para envío
 */
export function buildWhatsAppMessage(template, data) {
  return template
    .replace(/{{CLIENTE}}/g,         data.clientName    || 'Cliente')
    .replace(/{{FIGURA}}/g,          data.figureName    || 'Figura 3D')
    .replace(/{{MATERIAL}}/g,        data.material      || 'PLA')
    .replace(/{{PESO}}/g,            data.weightGrams   || '0')
    .replace(/{{TIEMPO}}/g,          data.hours         || '0')
    .replace(/{{COSTO_MATERIAL}}/g,  data.materialCost  || '0')
    .replace(/{{COSTO_LUZ}}/g,       data.electricityCost || '0')
    .replace(/{{COSTO_ADICIONAL}}/g, data.additionalCost  || '0')
    .replace(/{{MANO_OBRA}}/g,       data.laborCost     || '0')
    .replace(/{{PRECIO}}/g,          data.finalPrice    || '0')
    .replace(/{{MARGEN}}/g,          data.margin        || '0');
}

/**
 * Genera la URL de WhatsApp con el mensaje preformateado.
 *
 * @param {string} phone   - Número de teléfono con código de país (sin +)
 * @param {string} message - Texto del mensaje
 * @returns {string} URL lista para abrir WhatsApp Web o App
 */
export function generateWhatsAppUrl(phone, message) {
  const encodedMessage = encodeURIComponent(message);
  const cleanPhone = phone ? phone.replace(/\D/g, '') : '';

  if (cleanPhone) {
    return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
  }
  // Sin número de teléfono: abre WhatsApp con el mensaje listo para elegir contacto
  return `https://wa.me/?text=${encodedMessage}`;
}
